import { useRef, useEffect, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  useWindowDimensions,
  Platform,
  Animated,
  StyleSheet,
  Image,
  Pressable,
  Linking,
} from "react-native";
import { Link, useRouter } from "expo-router";
import Head from "expo-router/head";
import { Ionicons } from "@expo/vector-icons";
import { PartnersGrid } from "../../components/PartnersGrid";
import { PublicNav } from "../../components/PublicNav";

// Module-level constants used by the StyleSheet block at the bottom of the
// file (which runs at module load time and can't read a React hook). These
// are STATIC — they reflect the viewport at first page load only.
const { width: SCREEN_WIDTH } = Dimensions.get("window");
const isWide = SCREEN_WIDTH >= 768;

// Reactive hook used inside components so JSX (visibility, flexDirection,
// gaps, etc.) re-renders on browser resize without a full reload.
function useIsWide(): boolean {
  return useWindowDimensions().width >= 768;
}

// ─── Brand colors ────────────────────────────────────────────────────────────
const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const LIGHT_GRAY = "#F8F9FA";
const TEXT_MUTED = "#6B7280";

// ─── FadeIn animation wrapper ─────────────────────────────────────────────────
function FadeIn({
  children,
  delay = 0,
  style,
}: {
  children: React.ReactNode;
  delay?: number;
  style?: object;
}) {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(20)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 600,
        delay,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: 600,
        delay,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  return (
    <Animated.View style={[{ opacity, transform: [{ translateY }] }, style]}>
      {children}
    </Animated.View>
  );
}


// ─── Hero Video (web) / Gradient fallback (native) ────────────────────────────
function HeroBackground() {
  if (Platform.OS === "web") {
    return (
      <View style={StyleSheet.absoluteFillObject} pointerEvents="none">
        {/* @ts-ignore — raw HTML video element on web */}
        <video
          src="/hero-video-v2.mp4"
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        {/* Dark overlay */}
        <View
          style={[
            StyleSheet.absoluteFillObject,
            { backgroundColor: "rgba(17, 28, 48, 0.80)" },
          ]}
        />
      </View>
    );
  }
  // Native: just a gradient-like dark bg
  return (
    <View
      style={[
        StyleSheet.absoluteFillObject,
        { backgroundColor: NAVY_DARK },
      ]}
      pointerEvents="none"
    />
  );
}

// ─── Feature card ─────────────────────────────────────────────────────────────
function FeatureCard({
  icon,
  title,
  body,
  delay,
}: {
  icon: string;
  title: string;
  body: string;
  delay: number;
}) {
  const isWide = useIsWide();
  return (
    <FadeIn delay={delay} style={isWide ? styles.featureCardWide : styles.featureCard}>
      <View style={styles.featureIconBadge}>
        <Text style={styles.featureIcon}>{icon}</Text>
      </View>
      <Text style={styles.featureTitle}>{title}</Text>
      <Text style={styles.featureBody}>{body}</Text>
    </FadeIn>
  );
}

// ─── Step block ───────────────────────────────────────────────────────────────
function Step({ num, text }: { num: string; text: string }) {
  return (
    <View style={styles.step}>
      <View style={styles.stepNum}>
        <Text style={styles.stepNumText}>{num}</Text>
      </View>
      <Text style={styles.stepText}>{text}</Text>
    </View>
  );
}

// ─── Stat block ───────────────────────────────────────────────────────────────
function Stat({ value, label }: { value: string; label: string }) {
  return (
    <View style={styles.statBlock}>
      <Text style={styles.statValue}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );
}

// ─── Screenshot placeholder ───────────────────────────────────────────────────
function ScreenshotImage({ screenUrl, alt }: { screenUrl: string; alt: string }) {
  const isWide = useIsWide();
  return (
    <View style={{
      width: isWide ? 400 : 320,
      borderRadius: 20,
      overflow: "hidden",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 10 },
      shadowOpacity: 0.15,
      shadowRadius: 20,
      elevation: 8,
      backgroundColor: "#fff",
    }}>
      {Platform.OS === "web" ? (
        <img src={screenUrl} alt={alt} style={{ width: "100%", height: "auto", display: "block", borderRadius: 20 } as any} />
      ) : (
        <Image source={{ uri: `https://dashhound.app${screenUrl}` }} style={{ width: "100%", aspectRatio: 0.54 }} resizeMode="cover" />
      )}
    </View>
  );
}

function FeatureShowcase({ title, description, screenUrl, imageAlt, reverse }: {
  title: string; description: string; screenUrl: string; imageAlt: string; reverse?: boolean;
}) {
  const isWide = useIsWide();
  return (
    <FadeIn delay={100}>
      <View style={{
        flexDirection: isWide ? (reverse ? "row-reverse" : "row") : "column",
        alignItems: "center",
        gap: isWide ? 60 : 30,
        marginBottom: isWide ? 80 : 50,
        maxWidth: 1000,
        width: "100%",
        alignSelf: "center" as any,
      }}>
        <ScreenshotImage screenUrl={screenUrl} alt={imageAlt} />
        <View style={{ flex: 1, alignItems: isWide ? "flex-start" : "center", maxWidth: 450 }}>
          <Text style={{
            fontSize: isWide ? 28 : 22, fontWeight: "800", color: NAVY,
            marginBottom: 14, textAlign: isWide ? "left" : "center",
          }}>{title}</Text>
          <Text style={{
            fontSize: 16, color: "#4B5563", lineHeight: 26,
            textAlign: isWide ? "left" : "center",
          }}>{description}</Text>
        </View>
      </View>
    </FadeIn>
  );
}

// ─── Main landing page ────────────────────────────────────────────────────────
export default function LandingScreen() {
  const router = useRouter();
  const isWide = useIsWide();
  const scrollRef = useRef<ScrollView>(null);
  const sectionRefs = useRef<Record<string, number>>({});

  function handleScrollTo(section: string) {
    const y = sectionRefs.current[section];
    if (y !== undefined && scrollRef.current) {
      scrollRef.current.scrollTo({ y, animated: true });
    }
  }

  return (
    <View style={{ flex: 1, backgroundColor: NAVY_DARK }}>
      {/* Head only renders on web — on native it errors without the expo-router
          Config Plugin origin (which we don't need since the native app has no
          equivalent of HTML metadata or browser-tab title). */}
      {Platform.OS === "web" && (
        <Head>
          <title>Dash-Hound — Fast CAT Tracker App for Handlers</title>
          <meta name="description" content="The free companion app for Fast CAT. Log every run, see live breed rankings across 290 breeds, find events near you, and watch your dog climb the leaderboard. Built by a handler, for handlers." />
          <meta property="og:title" content="Dash-Hound — Fast CAT Tracker App for Handlers" />
          <meta property="og:description" content="Free Fast CAT companion app. Log runs, see live breed rankings, find events nearby, and watch your dog climb the leaderboard." />
          <meta property="og:image" content="https://dashhound.app/og-image.jpg" />
          <meta property="og:url" content="https://dashhound.app" />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Dash-Hound — Fast CAT Tracker App for Handlers" />
          <meta name="twitter:description" content="Free Fast CAT companion app. Log runs, see live breed rankings, find events nearby, and watch your dog climb the leaderboard." />
          <meta name="twitter:image" content="https://dashhound.app/og-image.jpg" />
        </Head>
      )}
      {/* Sticky navbar */}
      <PublicNav />

      <ScrollView
        ref={scrollRef}
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* ── 1. HERO ─────────────────────────────────────────── */}
        <View style={styles.hero}>
          <HeroBackground />

          <FadeIn delay={0} style={{ alignItems: "center", marginBottom: 15 }}>
            {Platform.OS === "web" ? (
              <img src="/logo-full.png" alt="Dash-Hound" style={{ height: isWide ? 180 : 130, objectFit: "contain" } as any} />
            ) : (
              <Image
                source={require("../../assets/logo.png")}
                style={{ height: isWide ? 180 : 130, width: isWide ? 360 : 260 }}
                resizeMode="contain"
              />
            )}
          </FadeIn>

          <FadeIn delay={120}>
            <Text style={styles.heroTitle}>
              Track Your{"\n"}
              <Text style={{ color: ORANGE }}>Fast CAT Journey</Text>
            </Text>
          </FadeIn>

          <FadeIn delay={240} style={styles.heroSubWrap}>
            <Text style={styles.heroSub}>
              The ultimate companion app for Fast CAT enthusiasts. Track runs,
              discover events, compare rankings, and watch your dog climb the
              leaderboard.
            </Text>
          </FadeIn>

          <FadeIn delay={360} style={styles.heroCTAs}>
            <TouchableOpacity
              style={styles.btnOrange}
              onPress={() => router.push("/(auth)/register/")}
              activeOpacity={0.85}
            >
              <Text style={styles.btnOrangeText}>{Platform.OS === "web" ? "Get the App" : "Get Started"}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.btnOutline}
              onPress={() => router.push("/(auth)/login/")}
              activeOpacity={0.85}
            >
              <Text style={styles.btnOutlineText}>Sign In</Text>
            </TouchableOpacity>
          </FadeIn>

          {Platform.OS === "web" && (
            <FadeIn delay={480} style={{ flexDirection: "row", justifyContent: "center", alignItems: "flex-start", gap: 12, marginTop: 20 }}>
              <a href="https://apps.apple.com/us/app/dash-hound/id6762554245" target="_blank" rel="noopener noreferrer" style={{ display: "block" } as any}>
                <img src="/appstore-badge.png" alt="Download on the App Store" style={{ height: 44, objectFit: "contain" } as any} />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.dashhound.app" target="_blank" rel="noopener noreferrer" style={{ display: "block" } as any}>
                <img src="/googleplay-badge.png" alt="Get it on Google Play" style={{ height: 44, objectFit: "contain" } as any} />
              </a>
            </FadeIn>
          )}
        </View>

        {/* ── 2. FEATURES ─────────────────────────────────────── */}
        <View
          style={styles.featuresSection}
          onLayout={(e) => {
            sectionRefs.current["features"] = e.nativeEvent.layout.y;
          }}
        >
          <FadeIn delay={0} style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              Everything You Need to Track Fast CAT
            </Text>
          </FadeIn>

          <View style={isWide ? styles.featureGridWide : styles.featureGrid}>
            <FeatureCard
              icon="⏱"
              title="Track Every Run"
              body="Log your times, see instant speed and points calculations. Track cumulative progress toward BCAT, DCAT, FCAT, and beyond."
              delay={100}
            />
            <FeatureCard
              icon="📍"
              title="Find Events Near You"
              body="Browse 1,000+ upcoming AKC Fast CAT events nationwide. Filter by state, search by location, get directions."
              delay={180}
            />
            <FeatureCard
              icon="🏆"
              title="Breed Rankings"
              body="See how your dog stacks up against 175,000+ ranked dogs across 290 breeds and 11 years of data, plus AKC lifetime standings."
              delay={260}
            />
            <FeatureCard
              icon="📈"
              title="Know Your Rank"
              body="See where your dog would rank in the current AKC breed rankings based on their best 3 runs average."
              delay={340}
            />
          </View>
        </View>

        {/* ── 3. HOW IT WORKS ─────────────────────────────────── */}
        <View style={styles.howSection}>
          <FadeIn delay={0} style={styles.sectionHeader}>
            <Text style={[styles.sectionTitle, { color: NAVY }]}>
              Get Started in Minutes
            </Text>
          </FadeIn>

          <View style={styles.stepsContainer}>
            <Step
              num="1"
              text="Create your free account and add your dog's profile"
            />
            <Step
              num="2"
              text="Log your Fast CAT runs — we calculate speed, points, and titles automatically"
            />
            <Step
              num="3"
              text="Track progress, find events, and see how you rank against the competition"
            />
          </View>
        </View>

        {/* ── 4. STATS ────────────────────────────────────────── */}
        <View
          style={styles.statsSection}
        >
          <View style={isWide ? styles.statsRowWide : styles.statsRow}>
            <Stat value="1,000+" label="Events" />
            <Stat value="175,000+" label="Rankings" />
            <Stat value="290" label="Breeds" />
            <Stat value="11" label="Years of Data" />
          </View>
        </View>

        {/* ── 5. APP PREVIEW ──────────────────────────────────── */}
        <View
          style={styles.previewSection}
        >
          <FadeIn delay={0} style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>See Dash-Hound in Action</Text>
          </FadeIn>

          <FeatureShowcase
            title="Your Dashboard, Your Dogs"
            description={"See all your dogs at a glance—total points, average speed, current titles, and exactly how many runs they need to reach the next milestone. Add your dog's photo, keep track of their AKC registration number, and jump straight into logging runs with a single tap.\n\nWhether you have one dog or a whole crew, every profile lives right on your dashboard with stats that update in real time after each run. Each card shows your dog's call name, registered name, breed, and AKC number—exactly the info you need at a trial, with no digging through screens.\n\nNo more spreadsheets. No more guesswork. No more wondering how many runs you've got left until the next title. Just open the app, see where you stand, and get back to the line."}
            screenUrl="/screenshots/home-1-dog-profile.png?v=5"
            imageAlt="Dog profile screen in Dash-Hound"
          />

          <FeatureShowcase
            title="Detailed Dog Profiles"
            description={"Take a closer look at each dog's performance with a full stats breakdown—personal best time, top speed, total points, total runs, and your Best 3 Average (calculated the exact same way the AKC ranks dogs).\n\nTrack progress toward every title—from BCAT to FCAT and well beyond—with easy-to-read visual progress bars. Compare Run 1 vs. Run 2 averages to spot when your dog performs best during the day, and see your current AKC rank alongside a projected rank based on your Best 3 Average—so you know exactly where you'll land once your dog's results post.\n\nShare your dog's profile publicly with one tap (or keep it private), import past runs from a CSV or screenshot, and pull up everything you need—stats, runs, titles, rank—from a single screen."}
            screenUrl="/screenshots/home-2-performance.png?v=4"
            imageAlt="Performance stats and title progression"
            reverse
          />

          <FeatureShowcase
            title="Complete Run History"
            description={"Every run your dog has ever done, all in one place. Date, time, speed, and points for each run—with clear Run 1 and Run 2 labels so you can track performance throughout the day and spot patterns in heat, surface, or time slot.\n\nTitle milestones are highlighted right in your history, so you can instantly see which run earned your BCAT, DCAT, FCAT, or any of the higher-tier titles. Tap any run to add a note—weather, surface conditions, what your dog ate that morning, or anything else worth remembering for next time.\n\nNeed to make a change? Edit or delete any run with a quick tap, and your cumulative points, average speed, and title progress recalculate instantly. Import existing runs from a CSV or even a screenshot of your AKC profile to backfill years of history in minutes."}
            screenUrl="/screenshots/home-3-run-history.png?v=5"
            imageAlt="Run history showing all logged runs"
          />

        </View>

        <View onLayout={(e) => { sectionRefs.current["events"] = e.nativeEvent.layout.y; }}
          style={{ backgroundColor: LIGHT_GRAY, paddingHorizontal: 24, paddingBottom: isWide ? 40 : 20 }}>
          <FeatureShowcase
            title="Find Events Near You"
            description={"Explore over 1,000 upcoming AKC Fast CAT events across the country, updated daily straight from the AKC. Search by state to scan a whole region, or by zip code with a customizable radius (50, 100, 150, or 200 miles) to find what's actually within driving distance.\n\nEvery listing shows the date, location, distance from you, entry fee, and a clear status badge—Upcoming, Entries Open, or Entries Closed—so you never waste a tap on a trial you can't enter.\n\nTap any event for the full breakdown: opening and closing dates, judge, eligible breeds, contact info for the event chair and secretary, and turn-by-turn directions launched right into your maps app. Everything you need to plan your next trial—without bouncing between five different websites."}
            screenUrl="/screenshots/home-4-events.png?v=3"
            imageAlt="Events list with details"
            reverse
          />
        </View>

        <View onLayout={(e) => { sectionRefs.current["rankings"] = e.nativeEvent.layout.y; }}
          style={{ backgroundColor: LIGHT_GRAY, paddingHorizontal: 24, paddingBottom: isWide ? 60 : 30 }}>
          <FeatureShowcase
            title="Breed Rankings"
            description={"Explore over 100,000 AKC Fast CAT rankings across 290 breeds, with a full decade of data from 2016 to today. See the fastest dogs in every breed, sorted by Best 3 Average—the same metric AKC uses—plus owner details and medal ribbons for the top five: gold, silver, and bronze for 1st, 2nd, and 3rd, with new 4th and 5th place ribbons to recognize every dog on the podium.\n\nSwitch between any individual year and the brand-new Lifetime Rankings view—the all-time AKC Fast CAT leaderboard pulled together for every breed. See who has dominated the breed across their entire career, not just this season. Pair it with the Year-to-Date view to track who's climbing the leaderboard right now.\n\nSearch 290+ breeds with a quick autocomplete—no scrolling through endless dropdowns. Whether you're checking where your Golden Retriever stands, sizing up the competition in your Australian Cattle Dog's bracket, or scouting rivals for the next national, Dash-Hound puts the full AKC Fast CAT leaderboard in your pocket. Updated daily, so you're always looking at the latest numbers."}
            screenUrl="/screenshots/home-5-rankings.png?v=2"
            imageAlt="Rankings search results"
          />
        </View>

        {/* ── New Features section ─────────────────────────── */}
        <View
          style={{
            backgroundColor: "#FFF7E6",
            paddingVertical: isWide ? 80 : 50,
            paddingHorizontal: 24,
            borderTopWidth: 4,
            borderTopColor: ORANGE,
            borderBottomWidth: 4,
            borderBottomColor: ORANGE,
          }}
        >
          <FadeIn delay={0} style={{ alignItems: "center", marginBottom: isWide ? 50 : 35 }}>
            <View
              style={{
                backgroundColor: ORANGE,
                paddingHorizontal: 14,
                paddingVertical: 6,
                borderRadius: 20,
                marginBottom: 14,
              }}
            >
              <Text style={{ fontSize: 12, fontWeight: "800", color: WHITE, letterSpacing: 1.2 }}>
                ✨ JUST LAUNCHED
              </Text>
            </View>
            <Text
              style={{
                fontSize: isWide ? 38 : 28,
                fontWeight: "800",
                color: NAVY,
                textAlign: "center",
                marginBottom: 10,
              }}
            >
              New Features in Dash-Hound
            </Text>
            <Text
              style={{
                fontSize: 16,
                color: "#6B4F1D",
                textAlign: "center",
                maxWidth: 640,
                lineHeight: 24,
              }}
            >
              Two brand-new insights built right into your dog's profile—designed to turn your run data into something actionable.
            </Text>
          </FadeIn>

          <FeatureShowcase
            title="Dash-Insight"
            description={"A personalized, real-time read on exactly where your dog stands and what it'll take to climb. Dash-Insight does the math for you—projected rank, gap to the next tier, and the exact time you need to run next to crack it.\n\nNo more squinting at the leaderboard wondering \"how close am I?\" The card tells you: \"You're projected #17 in Australian Cattle Dog, just 0.42 mph off the top 10. Run 7.53 seconds in your next 2 runs and you'll crack it.\" Concrete, achievable, and tied to your dog's actual personal best so the target stays realistic.\n\nDash-Insight also recommends specific upcoming events near you to chase that next milestone—\"Run it here\"—pulling from our live AKC event feed. One tap and you're looking at the trial that could push you onto the leaderboard."}
            screenUrl="/screenshots/feature-dash-insight.png?v=1"
            imageAlt="Dash-Insight card showing projected rank and next steps"
          />

          <FeatureShowcase
            title="Dash-Fact"
            description={"Know exactly how your dog stacks up against the best in the breed—not just the rank number, but the actual gap. Dash-Fact pulls the top 3 dogs in your breed's leaderboard and shows you the average time and speed you'd need to match them.\n\n\"Top 3 Australian Cattle Dogs run an average of 6.99s (29.24 mph). Your dog's best 3 avg: 7.70s (26.56 mph). 0.71s behind the top.\" That single line tells you more than scrolling through a ranking table ever could—it puts your dog's performance in honest, head-to-head context.\n\nUpdated every day from AKC's official rankings, Dash-Fact recalculates as soon as new results post and as soon as you log a new run. So the gap shrinks in real time as your dog gets faster."}
            screenUrl="/screenshots/feature-dash-fact.png?v=1"
            imageAlt="Dash-Fact card comparing your dog to top 3 in breed"
            reverse
          />
        </View>

        {/* ── Calling All Clubs section ──────────────────── */}
        <View
          style={{
            backgroundColor: NAVY,
            paddingVertical: isWide ? 80 : 50,
            paddingHorizontal: 24,
          }}
        >
          <FadeIn delay={0} style={{ alignItems: "center", marginBottom: isWide ? 40 : 28 }}>
            <View
              style={{
                backgroundColor: ORANGE,
                paddingHorizontal: 14,
                paddingVertical: 6,
                borderRadius: 20,
                marginBottom: 14,
              }}
            >
              <Text style={{ fontSize: 12, fontWeight: "800", color: WHITE, letterSpacing: 1.2 }}>
                🏆 FOR CLUBS & EVENT HOSTS
              </Text>
            </View>
            <Text
              style={{
                fontSize: isWide ? 38 : 28,
                fontWeight: "800",
                color: WHITE,
                textAlign: "center",
                marginBottom: 12,
              }}
            >
              Make Your Event Stand Out
            </Text>
            <Text
              style={{
                fontSize: 16,
                color: "#CBD5E1",
                textAlign: "center",
                maxWidth: 720,
                lineHeight: 24,
              }}
            >
              Every AKC-listed Fast CAT event already shows up in Dash-Hound automatically. But if you're a host club, you can do so much more—send us your branding and we'll turn your listing into a Featured Event that handlers actually notice.
            </Text>
          </FadeIn>

          {/* Phones mockup */}
          <View style={{ alignItems: "center", marginBottom: isWide ? 50 : 35, maxWidth: 1000, width: "100%", alignSelf: "center" as any }}>
            {Platform.OS === "web" ? (
              <img
                src="/screenshots/clubs-featured.jpg?v=1"
                alt="Featured event listings with club logos and ribbon previews"
                style={{ width: "100%", height: "auto", display: "block", borderRadius: 16 } as any}
              />
            ) : (
              <Image
                source={{ uri: "https://dashhound.app/screenshots/clubs-featured.jpg?v=1" }}
                style={{ width: "100%", aspectRatio: 1.41, borderRadius: 16 }}
                resizeMode="contain"
              />
            )}
          </View>

          {/* What to send list */}
          <View
            style={{
              maxWidth: 900,
              width: "100%",
              alignSelf: "center" as any,
              flexDirection: isWide ? "row" : "column",
              // flexWrap is only needed for the 2-column desktop grid. On mobile
              // (column + flexWrap) RN was wrapping the 4 cards into 2 tall
              // columns side by side instead of stacking them.
              ...(isWide ? { flexWrap: "wrap" as const } : {}),
              gap: 16,
              marginBottom: isWide ? 40 : 28,
            }}
          >
            {[
              {
                icon: "🎨",
                title: "Your Club Logo",
                body: "We'll display it right at the top of your event page, giving your club instant brand recognition the moment a handler taps in.",
              },
              {
                icon: "🏵️",
                title: "Ribbon Photos",
                body: "Send a photo of the ribbons handlers can earn at your event. We'll feature a 'What you could win' preview—nothing motivates entries like seeing the actual hardware.",
              },
              {
                icon: "⭐",
                title: "Featured Event Badge",
                body: "Clubs that send their materials get the orange Featured Event treatment in the events list, putting your trial above the standard listings on every search.",
              },
              {
                icon: "📝",
                title: "What Makes You Stand Out",
                body: "Why should a handler choose your trial? Surface, location perks, food trucks, on-site judging, hospitality—anything that makes your event special. We'll work it into your listing copy.",
              },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 60} style={{ width: isWide ? "48.5%" : "100%" }}>
                <View
                  style={{
                    backgroundColor: "rgba(255,255,255,0.06)",
                    borderRadius: 14,
                    padding: 28,
                    borderWidth: 1,
                    borderColor: "rgba(245,166,35,0.25)",
                    // `height: 100%` on wide screens keeps row cards equal-height.
                    // On mobile (column + flexWrap) it makes the first card take
                    // the full parent height and the rest wrap to a 2nd column.
                    ...(isWide ? { height: "100%" } : {}),
                    alignItems: "center",
                  }}
                >
                  <Text style={{ fontSize: 56, marginBottom: 14, textAlign: "center" }}>{item.icon}</Text>
                  <Text style={{ fontSize: 20, fontWeight: "800", color: ORANGE, marginBottom: 10, textAlign: "center" }}>
                    {item.title}
                  </Text>
                  <Text style={{ fontSize: 15, color: "#CBD5E1", lineHeight: 24, textAlign: "center" }}>
                    {item.body}
                  </Text>
                </View>
              </FadeIn>
            ))}
          </View>

          {/* CTA */}
          <FadeIn delay={300} style={{ alignItems: "center" }}>
            <Text style={{ fontSize: 16, color: "#CBD5E1", textAlign: "center", marginBottom: 18, maxWidth: 640, lineHeight: 24 }}>
              It's completely free—we just want to help handlers find your event and give your club the spotlight it deserves. Send us what you've got and we'll handle the rest.
            </Text>
            <TouchableOpacity
              onPress={() => router.push("/(auth)/support/" as any)}
              style={{
                backgroundColor: ORANGE,
                paddingVertical: 14,
                paddingHorizontal: 32,
                borderRadius: 12,
              }}
            >
              <Text style={{ color: WHITE, fontWeight: "800", fontSize: 16 }}>
                Submit Your Club's Info →
              </Text>
            </TouchableOpacity>
          </FadeIn>
        </View>

        {/* ── 6. FAQ ─────────────────────────────────────────── */}
        <View style={{ backgroundColor: NAVY_DARK, paddingVertical: isWide ? 80 : 50, paddingHorizontal: 24 }}>
          <FadeIn delay={0} style={{ alignItems: "center", marginBottom: 30 }}>
            <Text style={{ fontSize: isWide ? 36 : 28, fontWeight: "800", color: WHITE, textAlign: "center" }}>
              Frequently Asked Questions
            </Text>
            <Text style={{ fontSize: 16, color: "#94A3B8", textAlign: "center", marginTop: 10, maxWidth: 600 }}>
              Everything you need to know about Dash-Hound. If you need more help,{" "}
              <Text
                style={{ color: ORANGE, fontWeight: "700" }}
                onPress={() => router.push("/(auth)/support/")}
              >
                contact us through our support page
              </Text>
              .
            </Text>
          </FadeIn>

          <View style={{ maxWidth: 800, width: "100%", alignSelf: "center" as any, gap: 16 }}>
            {/* FAQ 1 */}
            <FadeIn delay={50}>
              <View style={{ backgroundColor: "rgba(255,255,255,0.08)", borderRadius: 14, padding: 24, borderWidth: 1, borderColor: "rgba(255,255,255,0.1)" }}>
                <Text style={{ fontSize: 18, fontWeight: "700", color: ORANGE, marginBottom: 10 }}>
                  Where can I download the Dash-Hound app?
                </Text>
                <Text style={{ fontSize: 15, color: "#CBD5E1", lineHeight: 24 }}>
                  Dash-Hound is available as a web app at dashhound.app—just sign up and start tracking from any device with a browser. The native iOS app is live on the Apple App Store and the Android app is live on Google Play. Your account and data sync seamlessly across web and mobile.
                </Text>
              </View>
            </FadeIn>

            {/* FAQ 2 */}
            <FadeIn delay={100}>
              <View style={{ backgroundColor: "rgba(255,255,255,0.08)", borderRadius: 14, padding: 24, borderWidth: 1, borderColor: "rgba(255,255,255,0.1)" }}>
                <Text style={{ fontSize: 18, fontWeight: "700", color: ORANGE, marginBottom: 10 }}>
                  How are speeds and points calculated in Dash-Hound?
                </Text>
                <Text style={{ fontSize: 15, color: "#CBD5E1", lineHeight: 24 }}>
                  Dash-Hound follows the official AKC Coursing Ability Test (CAT) and Fast CAT rules. Speed is calculated as 204.545 divided by your dog's time in seconds, rounded to the nearest hundredth. Points are based on speed and your dog's height handicap:
                </Text>
                <View style={{ marginTop: 10, marginLeft: 4, gap: 4 }}>
                  <Text style={{ fontSize: 15, color: "#CBD5E1", lineHeight: 22 }}>• Under 12 inches: 2.0×</Text>
                  <Text style={{ fontSize: 15, color: "#CBD5E1", lineHeight: 22 }}>• 12 to 17.99 inches: 1.5×</Text>
                  <Text style={{ fontSize: 15, color: "#CBD5E1", lineHeight: 22 }}>• 18 inches and over: 1.0×</Text>
                </View>
              </View>
            </FadeIn>

            {/* FAQ 3 */}
            <FadeIn delay={150}>
              <View style={{ backgroundColor: "rgba(255,255,255,0.08)", borderRadius: 14, padding: 24, borderWidth: 1, borderColor: "rgba(255,255,255,0.1)" }}>
                <Text style={{ fontSize: 18, fontWeight: "700", color: ORANGE, marginBottom: 10 }}>
                  Why might my totals differ slightly from what the AKC shows?
                </Text>
                <Text style={{ fontSize: 15, color: "#CBD5E1", lineHeight: 24 }}>
                  Usually it comes down to one of two things. AKC updates their official numbers on their own schedule, so a recent run might not be reflected on their end yet. And on our side, we round speeds and points to three decimal places for display — AKC keeps the full precision internally — which can show up as tiny gaps. If you're seeing more than that (a missing run, wrong points, or numbers that look way off), pull the run up in Dash-Hound and compare the time you entered against your ribbon. That catches the vast majority of mismatches. If your entries match the ribbon and AKC still has different numbers, that's a question for their records team.
                </Text>
              </View>
            </FadeIn>

            {/* FAQ 4 */}
            <FadeIn delay={200}>
              <View style={{ backgroundColor: "rgba(255,255,255,0.08)", borderRadius: 14, padding: 24, borderWidth: 1, borderColor: "rgba(255,255,255,0.1)" }}>
                <Text style={{ fontSize: 18, fontWeight: "700", color: ORANGE, marginBottom: 10 }}>
                  Where can I learn more about AKC Fast CAT?
                </Text>
                <Text style={{ fontSize: 15, color: "#CBD5E1", lineHeight: 24 }}>
                  The American Kennel Club offers detailed information about Fast CAT on their website, including rules, event listings, and an overview of the sport. Visit{" "}
                  {Platform.OS === "web" ? (
                    <a href="https://www.akc.org/sports/coursing/fast-cat/" target="_blank" rel="noopener noreferrer" style={{ color: "#F5A623", fontWeight: "600", textDecoration: "none" } as any}>akc.org/sports/coursing/fast-cat</a>
                  ) : (
                    <Text style={{ color: ORANGE, fontWeight: "600" }}>akc.org/sports/coursing/fast-cat</Text>
                  )}
                  {" "}to learn more.
                </Text>
              </View>
            </FadeIn>

            {/* FAQ 5 */}
            <FadeIn delay={250}>
              <View style={{ backgroundColor: "rgba(255,255,255,0.08)", borderRadius: 14, padding: 24, borderWidth: 1, borderColor: "rgba(255,255,255,0.1)" }}>
                <Text style={{ fontSize: 18, fontWeight: "700", color: ORANGE, marginBottom: 10 }}>
                  How does Dash-Hound estimate runs to my next title?
                </Text>
                <Text style={{ fontSize: 15, color: "#CBD5E1", lineHeight: 24 }}>
                  Dash-Hound looks at your dog's logged runs to calculate their average points per run, then compares that to how many points are left until the next title milestone (BCAT at 150, DCAT at 500, FCAT at 1,000, and FCAT2+ every 500 points after). Based on that, it estimates how many more runs you'll need. The more data you log, the more accurate the estimate becomes.
                </Text>
              </View>
            </FadeIn>

            {/* FAQ 6 */}
            <FadeIn delay={300}>
              <View style={{ backgroundColor: "rgba(255,255,255,0.08)", borderRadius: 14, padding: 24, borderWidth: 1, borderColor: "rgba(255,255,255,0.1)" }}>
                <Text style={{ fontSize: 18, fontWeight: "700", color: ORANGE, marginBottom: 10 }}>
                  Is Dash-Hound free to use?
                </Text>
                <Text style={{ fontSize: 15, color: "#CBD5E1", lineHeight: 24 }}>
                  Yes—Dash-Hound is completely free. You can create an account, add your dogs, log runs, browse events, and explore breed rankings at no cost. Your data is always saved and synced, whether you're using the web app or mobile. Built by dog people, for dog people.
                </Text>
              </View>
            </FadeIn>
          </View>
        </View>

        {/* ── 6.5 Partners ───────────────────────────────── */}
        <View style={{ backgroundColor: WHITE, paddingVertical: isWide ? 70 : 50, paddingHorizontal: 24 }}>
          <FadeIn delay={0} style={{ alignItems: "center", marginBottom: 32 }}>
            <Text style={{ fontSize: isWide ? 32 : 26, fontWeight: "800", color: NAVY, textAlign: "center", letterSpacing: -0.5 }}>
              Partners
            </Text>
          </FadeIn>

          <PartnersGrid isWide={isWide} />
        </View>

        {/* ── 7. CTA ──────────────────────────────────────────── */}
        <View style={styles.ctaSection}>
          <FadeIn delay={0} style={{ alignItems: "center" }}>
            <Text style={styles.ctaTitle}>
              Ready to Track Your{"\n"}Fast CAT Journey?
            </Text>
            <Text style={styles.ctaBody}>
              Create your free account and start tracking today.
            </Text>
            <TouchableOpacity
              style={{ backgroundColor: NAVY, paddingVertical: 16, paddingHorizontal: 40, borderRadius: 30, marginTop: 8 }}
              onPress={() => router.push("/(auth)/register/")}
              activeOpacity={0.85}
            >
              <Text style={{ color: WHITE, fontSize: 18, fontWeight: "700" }}>Get Started Free</Text>
            </TouchableOpacity>
          </FadeIn>
        </View>

        {/* ── 8. FOOTER ───────────────────────────────────────── */}
        <View style={styles.footer}>
          <View style={{
            flexDirection: isWide ? "row" : "column",
            justifyContent: "space-between",
            alignItems: isWide ? "flex-start" : "center",
            maxWidth: 1100,
            width: "100%",
            alignSelf: "center",
            gap: isWide ? 0 : 30,
            marginBottom: 30,
          }}>
            {/* Column 1: Logo */}
            <View style={{ alignItems: isWide ? "flex-start" : "center", flex: 1 }}>
              {Platform.OS === "web" ? (
                <img src="/logo-full.png" alt="Dash-Hound" style={{ height: 130, objectFit: "contain" } as any} />
              ) : (
                <Image
                  source={require("../../assets/logo.png")}
                  style={{ height: 130, width: 300 }}
                  resizeMode="contain"
                />
              )}
            </View>

            {/* Column 2: Navigation */}
            <View style={{ alignItems: isWide ? "flex-start" : "center", flex: 1 }}>
              <Text style={{ fontSize: 14, fontWeight: "700", color: ORANGE, marginBottom: 14, textTransform: "uppercase", letterSpacing: 1 }}>Navigation</Text>
              <TouchableOpacity onPress={() => handleScrollTo("features")} style={{ marginBottom: 10 }}>
                <Text style={styles.footerLink}>Features</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => handleScrollTo("events")} style={{ marginBottom: 10 }}>
                <Text style={styles.footerLink}>Events</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => handleScrollTo("rankings")} style={{ marginBottom: 10 }}>
                <Text style={styles.footerLink}>Rankings</Text>
              </TouchableOpacity>
              <Link href="/(auth)/support/" asChild>
                <TouchableOpacity style={{ marginBottom: 10 }}>
                  <Text style={styles.footerLink}>Support</Text>
                </TouchableOpacity>
              </Link>
              <Link href="/(auth)/privacy/" asChild>
                <TouchableOpacity style={{ marginBottom: 10 }}>
                  <Text style={styles.footerLink}>Privacy Policy</Text>
                </TouchableOpacity>
              </Link>
              <Link href="/(auth)/terms/" asChild>
                <TouchableOpacity style={{ marginBottom: 10 }}>
                  <Text style={styles.footerLink}>Terms of Service</Text>
                </TouchableOpacity>
              </Link>
            </View>

            {/* Column 3: App Store badges */}
            {Platform.OS === "web" && (
              <View style={{ alignItems: isWide ? "flex-end" : "center", flex: 1, marginTop: isWide ? 0 : 20 }}>
                <Text style={{ fontSize: 14, fontWeight: "700", color: ORANGE, marginBottom: 14, textTransform: "uppercase", letterSpacing: 1 }}>Get the App</Text>
                <a href="https://apps.apple.com/us/app/dash-hound/id6762554245" target="_blank" rel="noopener noreferrer" style={{ marginBottom: 10, display: "block" } as any}>
                  <img src="/appstore-badge.png" alt="Download on the App Store" style={{ height: 44, objectFit: "contain" } as any} />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.dashhound.app" target="_blank" rel="noopener noreferrer" style={{ display: "block" } as any}>
                  <img src="/googleplay-badge.png" alt="Get it on Google Play" style={{ height: 44, objectFit: "contain" } as any} />
                </a>
              </View>
            )}
          </View>

          <View style={{ borderTopWidth: 1, borderTopColor: "rgba(255,255,255,0.1)", paddingTop: 20, alignItems: "center", width: "100%" }}>
            <Text style={styles.footerCopy}>
              © 2026 Dash-Hound LLC. All rights reserved.
            </Text>
            <Text style={styles.footerDisclaimer}>
              AKC, Fast CAT, and related marks are trademarks of the American
              Kennel Club.
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },

  // ── Navbar ──
  navbar: {
    backgroundColor: NAVY,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.08)",
    zIndex: 100,
    ...(Platform.OS === "web"
      ? ({ position: "sticky", top: 0 } as any)
      : { paddingTop: 50 }),
  },
  navInner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: isWide ? 40 : 20,
    paddingVertical: 12,
    maxWidth: 1200,
    alignSelf: "center",
    width: "100%",
  },
  navLogoContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  navLogoImage: {
    width: 32,
    height: 32,
  },
  navLogoText: {
    color: WHITE,
    fontSize: 20,
    fontWeight: "800",
    letterSpacing: -0.5,
  },
  navLinks: {
    flexDirection: "row",
    gap: 32,
  },
  navLink: {
    color: "rgba(255,255,255,0.75)",
    fontSize: 15,
    fontWeight: "500",
  },
  navActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  navSignIn: {
    color: "rgba(255,255,255,0.75)",
    fontSize: 15,
    fontWeight: "500",
  },
  navCTA: {
    backgroundColor: ORANGE,
    paddingHorizontal: 18,
    paddingVertical: 8,
    borderRadius: 8,
  },
  navCTAText: {
    color: NAVY_DARK,
    fontSize: 14,
    fontWeight: "800",
  },
  navHamburger: {
    padding: 6,
    marginLeft: 4,
  },
  menuBackdrop: {
    ...(Platform.OS === "web"
      ? ({ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 } as any)
      : StyleSheet.absoluteFillObject),
    backgroundColor: "rgba(0,0,0,0.35)",
    zIndex: 90,
  },
  mobileMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: NAVY_DARK,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.08)",
    paddingVertical: 4,
    zIndex: 100,
  },
  mobileMenuItem: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.06)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  mobileMenuItemText: {
    color: WHITE,
    fontSize: 16,
    fontWeight: "500",
  },
  mobileMenuItemTextAccent: {
    color: ORANGE,
    fontSize: 16,
    fontWeight: "700",
  },

  // ── Hero ──
  hero: {
    minHeight: isWide ? 600 : 520,
    paddingTop: isWide ? 60 : 50,
    paddingBottom: 80,
    paddingHorizontal: 24,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    backgroundColor: NAVY_DARK,
  },
  heroBadge: {
    marginBottom: 24,
  },
  badgePill: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(245,166,35,0.15)",
    borderWidth: 1,
    borderColor: "rgba(245,166,35,0.35)",
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 100,
    gap: 8,
  },
  badgeDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: ORANGE,
  },
  badgeText: {
    color: ORANGE,
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  heroTitle: {
    fontSize: isWide ? 72 : 48,
    fontWeight: "900",
    color: WHITE,
    textAlign: "center",
    letterSpacing: -1.5,
    lineHeight: isWide ? 80 : 56,
  },
  heroSubWrap: {
    maxWidth: 560,
    marginTop: 20,
  },
  heroSub: {
    fontSize: isWide ? 18 : 15,
    color: "rgba(255,255,255,0.70)",
    textAlign: "center",
    lineHeight: 28,
  },
  heroCTAs: {
    flexDirection: "row",
    gap: 14,
    marginTop: 36,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  storeBadges: {
    flexDirection: "row",
    gap: 12,
    marginTop: 24,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  storeBadge: {
    backgroundColor: "rgba(255,255,255,0.08)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.15)",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  storeBadgeText: {
    color: "rgba(255,255,255,0.55)",
    fontSize: 12,
    fontWeight: "500",
  },

  // ── Buttons ──
  btnOrange: {
    backgroundColor: ORANGE,
    paddingHorizontal: 28,
    paddingVertical: 14,
    borderRadius: 10,
    shadowColor: ORANGE,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.35,
    shadowRadius: 16,
    elevation: 6,
  },
  btnOrangeText: {
    color: WHITE,
    fontSize: 16,
    fontWeight: "800",
    letterSpacing: 0.2,
  },
  btnOutline: {
    paddingHorizontal: 28,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: "rgba(255,255,255,0.35)",
  },
  btnOutlineText: {
    color: WHITE,
    fontSize: 16,
    fontWeight: "600",
  },

  // ── Features ──
  featuresSection: {
    backgroundColor: LIGHT_GRAY,
    paddingVertical: 72,
    paddingHorizontal: 24,
  },
  sectionHeader: {
    alignItems: "center",
    marginBottom: 40,
  },
  sectionTitle: {
    fontSize: isWide ? 36 : 26,
    fontWeight: "800",
    color: NAVY,
    textAlign: "center",
    letterSpacing: -0.5,
    maxWidth: 560,
  },
  featureGrid: {
    gap: 16,
    maxWidth: 600,
    alignSelf: "center",
    width: "100%",
  },
  featureGridWide: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
    maxWidth: 960,
    alignSelf: "center",
    justifyContent: "center",
  },
  featureCard: {
    backgroundColor: WHITE,
    borderRadius: 16,
    padding: 24,
    width: "100%",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  featureCardWide: {
    backgroundColor: WHITE,
    borderRadius: 16,
    padding: 28,
    width: "46%",
    minWidth: 280,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  featureIconBadge: {
    width: 72,
    height: 72,
    borderRadius: 18,
    backgroundColor: "rgba(245,166,35,0.12)",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  featureIcon: {
    fontSize: 39,
  },
  featureTitle: {
    color: NAVY,
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 8,
    letterSpacing: -0.2,
    textAlign: "center",
  },
  featureBody: {
    color: TEXT_MUTED,
    fontSize: 14,
    lineHeight: 22,
    textAlign: "center",
  },

  // ── How It Works ──
  howSection: {
    backgroundColor: WHITE,
    paddingVertical: 72,
    paddingHorizontal: 24,
  },
  stepsContainer: {
    maxWidth: 600,
    alignSelf: "center",
    width: "100%",
    gap: 28,
  },
  step: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 20,
  },
  stepNum: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: NAVY,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  stepNumText: {
    color: WHITE,
    fontSize: 18,
    fontWeight: "800",
  },
  stepText: {
    flex: 1,
    color: NAVY,
    fontSize: 16,
    lineHeight: 26,
    paddingTop: 8,
    fontWeight: "500",
  },

  // ── Stats ──
  statsSection: {
    backgroundColor: NAVY,
    paddingVertical: 60,
    paddingHorizontal: 24,
  },
  statsRow: {
    gap: 24,
    alignItems: "center",
  },
  statsRowWide: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 0,
    flexWrap: "wrap",
    alignItems: "center",
  },
  statBlock: {
    alignItems: "center",
    paddingHorizontal: isWide ? 48 : 24,
    paddingVertical: 12,
  },
  statValue: {
    fontSize: isWide ? 52 : 42,
    fontWeight: "900",
    color: ORANGE,
    letterSpacing: -1,
  },
  statLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: "rgba(255,255,255,0.55)",
    letterSpacing: 1.2,
    textTransform: "uppercase",
    marginTop: 4,
  },

  // ── App Preview ──
  previewSection: {
    backgroundColor: LIGHT_GRAY,
    paddingVertical: 72,
    paddingHorizontal: 24,
  },
  screenshots: {
    gap: 16,
    maxWidth: 600,
    alignSelf: "center",
    width: "100%",
  },
  screenshotsWide: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    maxWidth: 960,
    alignSelf: "center",
    justifyContent: "center",
  },
  screenshotBox: {
    backgroundColor: "#E2E8F0",
    borderRadius: 16,
    height: 200,
    width: isWide ? 210 : "100%",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#CBD5E1",
  },
  screenshotLabel: {
    color: "#94A3B8",
    fontSize: 13,
    fontWeight: "500",
  },

  // ── CTA ──
  ctaSection: {
    backgroundColor: ORANGE,
    paddingVertical: 80,
    paddingHorizontal: 24,
    alignItems: "center",
  },
  ctaTitle: {
    fontSize: isWide ? 40 : 28,
    fontWeight: "900",
    color: NAVY,
    textAlign: "center",
    letterSpacing: -0.8,
    lineHeight: isWide ? 50 : 36,
    marginBottom: 16,
  },
  ctaBody: {
    color: "rgba(27,42,74,0.7)",
    fontSize: 16,
    textAlign: "center",
    lineHeight: 26,
    marginBottom: 32,
    maxWidth: 440,
  },

  // ── Footer ──
  footer: {
    backgroundColor: NAVY_DARK,
    paddingVertical: 48,
    paddingHorizontal: 24,
    alignItems: "center",
    borderTopWidth: 1,
    borderTopColor: "rgba(255,255,255,0.08)",
    gap: 16,
  },
  footerLogoRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  footerLogoImage: {
    width: 36,
    height: 36,
  },
  footerLogoText: {
    color: WHITE,
    fontSize: 22,
    fontWeight: "900",
    letterSpacing: -0.5,
  },
  footerLinks: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
    justifyContent: "center",
  },
  footerLink: {
    color: "rgba(255,255,255,0.55)",
    fontSize: 14,
    fontWeight: "500",
  },
  footerCopy: {
    color: "rgba(255,255,255,0.35)",
    fontSize: 13,
    textAlign: "center",
  },
  footerDisclaimer: {
    color: "rgba(255,255,255,0.25)",
    fontSize: 11,
    textAlign: "center",
    maxWidth: 440,
    lineHeight: 18,
  },
});
